import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import SingleSelect from "components/Select/SingleSelect";
import { setDateRangePicTreeCommon, setDateRangePicTreeMinMax, setSelectedSeasonCommon, setSelectedAreaLayerCommon, getSeasonDataCommon } from "actions";

const HINAN_KUNREN_SEASON_ID = "cmt8v3v940001a4zc1b2v7nvx";
const LAYER_OPTIONS = [
  { name: "階層1", value: "japan_1_l1" },
  { name: "階層2", value: "japan_1_l2" },
  { name: "階層3", value: "japan_1_l3" },
];

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: 0,
    },
    chip: {
      margin: theme.spacing(0.5),
      fontSize: "0.75rem",
      height: 24,
    },
    autocomplete: {
      width: 510,
      marginTop: -8,
    },
    seasonTextField: {
      "& .MuiOutlinedInput-root": {
        padding: "1px 8px",
      },
      "& .MuiOutlinedInput-input": {
        height: "1.2em",
      },
    },
    singleSeasonWrapper: {
      width: "auto",
      display: "inline-block",
      marginTop: -24,
    },
  })
);

const PeriodCommon = ({
  setDateRangePicTreeCommon,
  setDateRangePicTreeMinMax,
  setSelectedSeasonCommon,
  setSelectedAreaLayerCommon,
  getSeasonDataCommon,
  seasonData,
  selectedSeason,
  dateRangePicTreeCommon,
  singlePeriod,
  selectedAreaLayerId,
  currentPage,
}) => {
  const classes = useStyles();
  const currentDateTime = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const todayFormatted = moment(today).format("YYYY-MM-DD");
  const normalizeDate = (value) =>
    moment.tz(value, "YYYY-MM-DD HH:mm", "Asia/Tokyo").format("YYYY-MM-DD");
  const getClampedRange = (startValue, endValue) => {
    const seasonStart = normalizeDate(startValue);
    const seasonEnd = normalizeDate(endValue);
    if (moment(todayFormatted).isBefore(seasonStart)) {
      return [todayFormatted, todayFormatted];
    }
    if (moment(todayFormatted).isAfter(seasonEnd)) {
      return [seasonStart, seasonEnd];
    }
    return [seasonStart, todayFormatted];
  };

  const seasonOptions = seasonData.map((season) => ({
    name: season.seasonName,
    value: season.seasonId,
  }));
  const [seasonOption, setSeasonOption] = useState({ name: "", value: "" });

  useEffect(() => {
    getSeasonDataCommon();
  }, []);

  useEffect(() => {
    if (seasonData.length > 0) {
      const selectedSeasonData = selectedSeason?.[0]?.seasonId
        ? seasonData.find((season) => season.seasonId === selectedSeason[0].seasonId)
        : null;
      if (selectedSeasonData) {
        setSeasonOption({
          name: selectedSeasonData.seasonName,
          value: selectedSeasonData.seasonId,
        });
        return;
      }

      const matchedSeasons = seasonData.filter((season) => {
        const startDate = season.seasonStart;
        const endDate = season.seasonEnd;
        return today >= startDate && today <= endDate;
      });
      if (!selectedSeason || selectedSeason.length === 0) {
        if (matchedSeasons.length > 0) {
          setSelectedSeasonCommon(matchedSeasons);
          setSeasonOption({
            name: matchedSeasons[0].seasonName,
            value: matchedSeasons[0].seasonId,
          });
          const earliestStart = matchedSeasons
            .map((s) => s.seasonStart)
            .reduce((a, b) => (a < b ? a : b));
          const latestEnd = matchedSeasons
            .map((s) => s.seasonEnd)
            .reduce((a, b) => (a > b ? a : b));

          const [rangeStart, rangeEnd] = getClampedRange(
            earliestStart,
            latestEnd
          );
          setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
          setDateRangePicTreeCommon([rangeStart, rangeEnd]);
        } else {
          setSelectedSeasonCommon([seasonData[0]]);
          setSeasonOption({
            name: seasonData[0].seasonName,
            value: seasonData[0].seasonId,
          });
          const [rangeStart, rangeEnd] = getClampedRange(
            seasonData[0]["seasonStart"],
            seasonData[0]["seasonEnd"]
          );
          setDateRangePicTreeCommon([rangeStart, rangeEnd]);
          setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
        }
      }
    }
  }, [seasonData]);

  const handleSelectionChange = (event, value) => {
    const uniqueValue = value.filter((option, index, self) => {
      const isDuplicate =
        self.filter((item) => item.seasonId === option.seasonId).length > 1;
      return !isDuplicate;
    });

    const validSelections = uniqueValue.filter(
      (option, index, self) =>
        self.findIndex((o) => o.seasonId === option.seasonId) === index
    );

    if (validSelections.length > 0) {
      const selectedSeasonsData = seasonData.filter((s) =>
        validSelections.some((item) => item.seasonId === s.seasonId)
      );

      const earliestStart = selectedSeasonsData
        .map((s) => s.seasonStart)
        .reduce((a, b) => (a < b ? a : b));

      const latestEnd = selectedSeasonsData
        .map((s) => s.seasonEnd)
        .reduce((a, b) => (a > b ? a : b));

      const [rangeStart, rangeEnd] = getClampedRange(
        earliestStart,
        latestEnd
      );
      setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
      setDateRangePicTreeCommon([rangeStart, rangeEnd]);

      // ステートを更新
      setSelectedSeasonCommon(validSelections);
    } else {
      setDateRangePicTreeMinMax([]);
      setDateRangePicTreeCommon([]);
    }
  };

  const handleChange = (event) => {
    const selectedSeasonData = seasonData.find(
      (season) => season.seasonId === event.target.value
    );
    if (!selectedSeasonData) {
      return;
    }

    setSeasonOption({
      name: selectedSeasonData.seasonName,
      value: selectedSeasonData.seasonId,
    });
    setSelectedSeasonCommon([selectedSeasonData]);
    const [rangeStart, rangeEnd] = getClampedRange(
      selectedSeasonData.seasonStart,
      selectedSeasonData.seasonEnd
    );
    setDateRangePicTreeCommon([rangeStart, rangeEnd]);
    setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
  };

  const handleLayerChange = (event) => {
    setSelectedAreaLayerCommon(event.target.value);
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          <Grid item spacing={1} style={{marginTop: 8}}>
            シーズン：
          </Grid>
          <Grid item>
            {singlePeriod && (
              <div className={classes.singleSeasonWrapper}>
                <SingleSelect
                  selected={seasonOption}
                  options={seasonOptions}
                  handleChange={handleChange}
                />
              </div>
            )}
            {!singlePeriod &&
            <Autocomplete
              multiple
              id="season-autocomplete"
              options={seasonData}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                `${option.seasonName} : ${option.seasonStart} ~ ${option.seasonEnd}`
              }
              renderOption={(option, { selected }) => {
                const isFutureSeason = moment(option.seasonStart).isAfter(currentDateTime);
                const isSelected = selectedSeason.some((selected) => selected.seasonId === option.seasonId);
                return (
                  <div>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={isSelected}
                      disabled={isFutureSeason}
                    />
                    {`${option.seasonName}: `}
                    <span
                      style={{ color: "gray", marginLeft: 16, fontSize: 13 }}
                    >
                      {`${option.seasonStart} ~ ${option.seasonEnd}`}
                    </span>
                  </div>
                );
              }}
              value={selectedSeason || []}
              onChange={handleSelectionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder=""
                  className={classes.seasonTextField}
                />
              )}
              renderTags={(selected, getTagProps) => {
                const visibleTags = selected.slice(0, 3);
                const extraCount = selected.length - visibleTags.length;
                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.seasonName}
                      size="small"
                      className={classes.chip}
                    />
                  )),
                  extraCount > 0 && (
                    <Typography
                      key="extra"
                      variant="body2"
                      style={{ marginLeft: 8, color: "gray" }}
                    >
                      +{extraCount}
                    </Typography>
                  ),
                ];
              }}
              disableClearable
              className={classes.autocomplete}
            />
            }
          </Grid>
          {singlePeriod &&
            currentPage === "/game/pictree/common/photos/by-area" &&
            selectedSeason?.[0]?.seasonId === HINAN_KUNREN_SEASON_ID && (
            <>
              <Grid item spacing={1} style={{ marginTop: 8 }}>
                階層：
              </Grid>
              <Grid item>
                <div style={{ width: 140, marginTop: -8 }}>
                  <SingleSelect
                    selected={{ value: selectedAreaLayerId }}
                    options={LAYER_OPTIONS}
                    handleChange={handleLayerChange}
                  />
                </div>
              </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

PeriodCommon.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.pictree.periodType,
  seasonData: state.pictreeCommon.seasonData,
  selectedSeason: state.pictreeCommon.selectedSeason,
  selectedAreaLayerId: state.pictreeCommon.selectedAreaLayerId,
  dateRangePicTreeCommon: state.page.dateRangePicTreeCommon,
  currentPage: state.page.currentPage,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTreeCommon: (param) => dispatch(setDateRangePicTreeCommon(param)),
  setDateRangePicTreeMinMax: (param) => dispatch(setDateRangePicTreeMinMax(param)),
  getSeasonDataCommon: () => dispatch(getSeasonDataCommon()),
  setSelectedSeasonCommon: (param) => dispatch(setSelectedSeasonCommon(param)),
  setSelectedAreaLayerCommon: (param) => dispatch(setSelectedAreaLayerCommon(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(PeriodCommon));
